import { Component, ElementRef, Injector, Optional, Self, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { AbstractComponent, FirebaseService, UploadTypeEnum } from 'lib-trend-core';

/**
 * Logo/avatar picker of the Webchat channel (RF-04), shared by the add-channel page and the edit
 * dialog through `formControlName="logoUrl"`.
 *
 * The image is uploaded to Firebase Storage — the same flow as the company logo and the user
 * photo — and the bound control receives its download URL, so `channel.metadata.logoUrl` still
 * holds a URL and neither the API nor the widget changes.
 */
@Component({
  selector: 'webchat-logo-upload-component',
  templateUrl: './webchat-logo-upload.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class WebchatLogoUploadComponent extends AbstractComponent implements ControlValueAccessor {

  // Same limits as the company logo (edit-basic-info-company).
  private static readonly MAX_SIZE_BYTES = 1024 * 1024;
  private static readonly ACCEPTED_TYPES = ['image/jpeg', 'image/png'];

  @ViewChild('fileInput', { static: true }) fileInput: ElementRef<HTMLInputElement>;

  logoUrl: string | null = null;
  uploading = false;
  disabled = false;

  // Logo uploaded here and not saved yet: removing it deletes the file. Removing the logo that was
  // already saved only clears the field, so cancelling the form never leaves the channel pointing
  // at a deleted image.
  private uploadedUrl: string | null = null;

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    injector: Injector,
    private firebaseService: FirebaseService,
    @Optional() @Self() private ngControl: NgControl,
  ) {
    super(injector);
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  writeValue(value: string | null): void {
    this.logoUrl = value || null;
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  pickFile(): void {
    if (!this.disabled && !this.uploading) {
      this.fileInput.nativeElement.click();
    }
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = ''; // lets the same file be picked again after a rejection
    if (!file) {
      return;
    }
    this.onTouched();

    if (!WebchatLogoUploadComponent.ACCEPTED_TYPES.includes(file.type)) {
      this.alertService.warning(this.t('channel.webchatLogoUpload.formatError'));
      return;
    }
    if (file.size > WebchatLogoUploadComponent.MAX_SIZE_BYTES) {
      this.alertService.warning(this.t('channel.webchatLogoUpload.sizeError'));
      return;
    }

    this.setUploading(true);
    try {
      const filename = `${this.getIDCurrentCompany()}/${new Date().getTime()}`;
      const snapshot: { url: string } = await this.firebaseService.uploadFile(file, filename, UploadTypeEnum.CHANNEL);
      this.uploadedUrl = snapshot.url;
      this.update(snapshot.url);
    } catch {
      this.alertService.error(this.t('channel.webchatLogoUpload.uploadError'));
    } finally {
      this.setUploading(false);
    }
  }

  remove(): void {
    if (this.logoUrl && this.logoUrl === this.uploadedUrl) {
      void this.firebaseService.deleteFile(this.logoUrl);
    }
    this.uploadedUrl = null;
    this.onTouched();
    this.update(null);
  }

  private update(value: string | null): void {
    this.logoUrl = value;
    this.onChange(value);
  }

  // While a file is uploading the bound control carries an `uploading` error, so the host form stays
  // invalid (Save disabled) until the URL arrives — otherwise the channel could be saved without it.
  private setUploading(uploading: boolean): void {
    this.uploading = uploading;
    const control = this.ngControl?.control;
    if (!control) {
      return;
    }
    if (uploading) {
      control.setErrors({ ...(control.errors ?? {}), uploading: true });
    } else {
      control.updateValueAndValidity();
    }
  }
}
